import React, { useState, useRef, FormEvent } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import arrowOutlined from "@/assets/arrow-outlined.svg";
import { Url } from "@/helpers/url";
import { useServices } from "@/services-context";
import styles from "./password-recovery.module.scss";
import fonts from "@/style/fonts.module.scss";
import EnterHandler from "@/elements-react/enter-handler";
import { FooterElement } from "@/elements-react/footer-element/FooterElement";
import { FixLink } from "@/elements-react/fix-link";
import { FixButton } from "@/elements-react/fix-button";
import { Input } from "@/uikit";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel.ts";
import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { Services } from "@/services/services";
import {Header} from "@/elements-react/catalog-layout/header";

const fetchPageData = async (params: { services: Services, location: LocationHook, route: RouteHook }) => {
  const { services, location, route } = params;

  await services.initialize.prepare({
    location: location,
    route: route,
  });
};

const PasswordRecoveryPage = pageSuspense(() => {
  const route = useRoute();
  const location = useLocation();
  const services = useServices([CUSTOMER_CHANNEL_LISTENER_NAME]);

  useSuspenseLoader({
    key: "recovery-route",
    loader: () => fetchPageData({
      services: services,
      location: location,
      route: route
    })
  });

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState("");
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmedPassword: "",
  });
  const formRef = useRef<HTMLFormElement>(null);
  const { layoutType, defaultLayoutStyleType } = useLayoutType();

  const resetPasswordToken = (route.params?.token as string) || "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (passwordResetError) {
      setPasswordResetError("");
    }
  };

  const handleClear = (name: keyof typeof formData) => {
    setFormData((prev) => ({ ...prev, [name]: "" }));
    setPasswordResetError("");
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();

    const { newPassword, confirmedPassword } = formData;

    if (!newPassword || !confirmedPassword) {
      return;
    }

    if (!resetPasswordToken) {
      setPasswordResetError(t("vy0rzkl108ag908k.incorrect-username", "Incorrect Username"));
      return;
    }

    setPasswordResetError("");
    setLoading(true);

    try {
      const result = await services.customerChannel.resetPassword({
        newPassword,
        confirmedPassword,
        resetPasswordToken,
      });

      setLoading(false);

      if (!result.success) {
        setPasswordResetError(result.errors?.[0]?.message ?? t("vy0rzkl108ag908k.incorrect-username", "Incorrect Username"));
        return;
      }

      redirectToLoginPage();
    } catch (error) {
      setLoading(false);
      setPasswordResetError(t("vy0rzkl108ag908k.incorrect-username", "Incorrect Username"));
    }
  };

  const redirectToLoginPage = () => {
    Url.route(Url.to("login-page"));
  };

  const renderContinueShoppingLink = () => (
    <FixLink className="continueShoppingLink" href={Url.to("home-page-with-lang")} type={defaultLayoutStyleType}>
      <Icon src={arrowOutlined} />
      <span>{t("k0q7l2w2q1856v4o.continue-shopping", "Continue shopping")}</span>
    </FixLink>
  );

  const renderPage = () => (
    <div className={styles.pageContainer}>
      {layoutType === LayoutTypes.TYPE_MOBILE && <div className={styles.header}>{renderContinueShoppingLink()}</div>}
      <div className={styles.formContainer}>
        <EnterHandler onEnter={handleSubmit}>
          <form className={styles.passwordResetForm} name="password-reset" ref={formRef} onSubmit={handleSubmit}>
            <div>
              <span className={classNames(styles.passwordResetHeading, fonts["font-heading-3"])}>
                {t("s0hdh7oc8j01ydvf.forgot-password-text", "Forgot your password?")}
              </span>
            </div>
            <div className={styles.inputsContainer}>
              <Input
                name="newPassword"
                type="password"
                placeholder={t("ifm2plg74o52mxbv.create-new-password", "Create new password")}
                required={true}
                clearable={true}
                value={formData.newPassword}
                onInput={handleChange}
                onClear={() => handleClear("newPassword")}
              />
              <Input
                name="confirmedPassword"
                type="password"
                placeholder={t("ld981fe5hys8kvws.confirm-your-password", "Confirm your password")}
                customError={passwordResetError}
                required={true}
                clearable={true}
                value={formData.confirmedPassword}
                onInput={handleChange}
                onClear={() => handleClear("confirmedPassword")}
              />
            </div>
            <FixButton onClick={handleSubmit} variant="primary" type="submit" fullWidth={true} loading={loading}>
              {t("min5x9lv5wevap71.change-password", "Change password")}
            </FixButton>
          </form>
        </EnterHandler>
      </div>
    </div>
  );

  const renderMobile = () => (
    <>
      <Header simple={true}></Header>
      {renderPage()}
    </>
  );

  const renderDesktop = () => (
    <>
      <Header simple={true}></Header>
      {renderPage()}
      <FooterElement />
    </>
  );

  return (
    <div className={classNames(styles.root)}>
      <div className={classNames(styles[defaultLayoutStyleType])}>{layoutType === LayoutTypes.TYPE_MOBILE ? renderMobile() : renderDesktop()}</div>
    </div>
  );
});

export { PasswordRecoveryPage };
